<template>
  <section :aria-labelledby="`${panelId}-heading`">
    <div class="max-w-3xl">
      <h2
        :id="`${panelId}-heading`"
        class="text-2xl text-black dark:text-white"
      >
        {{ $t("pages.guide.toursGallery") }}
      </h2>
    </div>

    <div
      v-if="navigationItems.length"
      class="mt-6 max-w-2xl"
      data-test="guide-gallery-tour-selector"
    >
      <p
        :id="`${panelId}-tour-label`"
        class="mb-2 block text-xs font-bold tracking-wide text-grey uppercase dark:text-grey-light"
      >
        {{
          $t(
            navigationItems.length > 1
              ? "pages.guide.galleryChooseTour"
              : "pages.guide.galleryTourLabel",
          )
        }}
      </p>
      <div
        ref="selectorRoot"
        class="relative"
        @focusout="handleSelectorFocusOut"
        @keydown.esc.stop.prevent="closeSelector(true)"
      >
        <button
          v-if="navigationItems.length > 1"
          :id="`${panelId}-tour-select`"
          ref="selectorTrigger"
          type="button"
          aria-haspopup="listbox"
          :aria-expanded="selectorOpen"
          :aria-controls="`${panelId}-tour-options`"
          :aria-labelledby="`${panelId}-tour-label ${panelId}-tour-value`"
          class="min-h-14 w-full cursor-pointer rounded-base border border-light-200 bg-white py-3 pr-12 pl-4 text-left text-base font-bold text-black shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:border-grey-dark dark:bg-dark dark:text-white dark:focus-visible:outline-gold"
          @click="selectorOpen ? closeSelector() : openSelector()"
          @keydown.down.prevent="openSelector"
          @keydown.up.prevent="openSelector"
        >
          <span :id="`${panelId}-tour-value`">{{
            galleryLabel(Number(activeGalleryId))
          }}</span>
          <Icon
            icon="chevron-down"
            class="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-base"
            aria-hidden="true"
          />
        </button>
        <p
          v-else
          class="min-h-14 w-full rounded-base border border-light-200 bg-white px-4 py-3 text-base font-bold text-black dark:border-grey-dark dark:bg-dark dark:text-white"
          data-test="guide-gallery-single-tour"
        >
          {{ navigationItems[0]?.label }}
        </p>
        <div
          v-if="selectorOpen && navigationItems.length > 1"
          :id="`${panelId}-tour-options`"
          role="listbox"
          :aria-labelledby="`${panelId}-tour-label`"
          class="absolute top-full right-0 left-0 z-30 mt-2 max-h-60 overflow-y-auto overscroll-contain rounded-base border border-light-200 bg-white p-2 shadow-xl dark:border-grey-dark dark:bg-dark"
        >
          <button
            v-for="item in navigationItems"
            :key="item.id"
            type="button"
            role="option"
            :aria-selected="activeGalleryId === item.id"
            :tabindex="focusedOptionId === item.id ? 0 : -1"
            :data-tour-id="item.id"
            class="flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-md px-4 py-3 text-left text-sm font-bold whitespace-normal transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-gold"
            :class="
              activeGalleryId === item.id
                ? 'bg-black text-white dark:bg-white dark:text-black'
                : 'text-black hover:bg-light-200 dark:text-white dark:hover:bg-grey-dark'
            "
            @click="
              selectGallery(item.id);
              closeSelector(true);
            "
            @keydown="handleOptionKeydown($event, item.id)"
          >
            <Icon
              icon="route"
              class="shrink-0 text-lg leading-none"
              aria-hidden="true"
            />
            <span>{{ item.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <GuideEmptyState
      v-if="!activeGallery"
      class="mt-6"
      icon="layout-left"
      :title="$t('pages.guide.galleryEmptyTitle')"
      :description="$t('pages.guide.galleryEmptyDescription')"
    />

    <div
      v-for="gallery in galleries"
      v-show="activeGalleryId === String(gallery.product_id)"
      :id="`${panelId}-gallery-${gallery.product_id}-panel`"
      :key="gallery.product_id"
      role="region"
      :aria-label="galleryLabel(gallery.product_id)"
      tabindex="0"
      class="mt-6 scroll-mt-20 pb-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
    >
      <ProductMediaCarousel
        v-if="activeGalleryId === String(gallery.product_id)"
        :key="gallery.product_id"
        v-model:current-slide="activeMaterialIndex"
        class="text-black dark:text-white"
        :slides="gallery.items"
        :show-captions="true"
        :enable-fullscreen="true"
        :is-active="isActive"
        layout="guide"
      />
      <ol
        v-if="activeGalleryId === String(gallery.product_id)"
        :aria-label="$t('pages.guide.galleryMaterialListLabel')"
        class="grid gap-2 sm:grid-cols-2"
        :class="gallery.items.length > 1 ? 'mt-16' : 'mt-4'"
        data-test="guide-gallery-material-list"
      >
        <li v-for="(item, index) in gallery.items" :key="`${item.id}-${index}`">
          <button
            type="button"
            class="flex min-h-11 w-full cursor-pointer items-start gap-2 rounded-base border px-3 py-2 text-left text-sm leading-relaxed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-gold"
            :class="
              index === activeMaterialIndex
                ? 'border-gold bg-gold/15 font-bold text-black dark:text-white'
                : 'border-light-200 bg-white text-black hover:bg-light-200 dark:border-grey-dark dark:bg-dark dark:text-white dark:hover:bg-grey-dark'
            "
            :aria-current="index === activeMaterialIndex ? 'true' : undefined"
            :aria-label="materialAccessibleLabel(item, index)"
            data-test="guide-gallery-material-link"
            @click="selectMaterial(index)"
          >
            <span class="shrink-0" aria-hidden="true">{{ index + 1 }}.</span>
            <span
              v-if="materialCaption(item)"
              class="min-w-0 break-words"
              aria-hidden="true"
              >{{ materialCaption(item) }}</span
            >
          </button>
        </li>
      </ol>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type {
  Guide,
  GuideGalleryGroup,
  GuideMaterial,
  GuideNavigationItem,
} from "@/types";

const props = withDefaults(
  defineProps<{
    guide: Guide;
    galleries: GuideGalleryGroup[];
    panelId: string;
    isActive?: boolean;
  }>(),
  { isActive: true },
);

const { t } = useI18n();
const activeGalleryId = ref("");
const activeMaterialIndex = ref(0);
const selectorRoot = ref<HTMLElement | null>(null);
const selectorTrigger = ref<HTMLButtonElement | null>(null);
const selectorOpen = ref(false);
const focusedOptionId = ref("");

const focusOption = async (id: string) => {
  focusedOptionId.value = id;
  await nextTick();
  if (!selectorOpen.value) return;
  const options =
    selectorRoot.value?.querySelectorAll<HTMLButtonElement>('[role="option"]');
  Array.from(options ?? [])
    .find((option) => option.dataset.tourId === id)
    ?.focus();
};
const openSelector = () => {
  if (navigationItems.value.length < 2) return;
  selectorOpen.value = true;
  void focusOption(activeGalleryId.value);
};
const closeSelector = (restoreFocus = false) => {
  selectorOpen.value = false;
  if (restoreFocus) selectorTrigger.value?.focus();
};
const handleOptionKeydown = (event: KeyboardEvent, id: string) => {
  const items = navigationItems.value;
  const index = items.findIndex((item) => item.id === id);
  const target =
    event.key === "ArrowDown"
      ? (index + 1) % items.length
      : event.key === "ArrowUp"
        ? (index - 1 + items.length) % items.length
        : event.key === "Home"
          ? 0
          : event.key === "End"
            ? items.length - 1
            : -1;
  if (target < 0) return;
  event.preventDefault();
  void focusOption(items[target].id);
};
const handleOutsidePointer = (event: PointerEvent) => {
  if (!selectorRoot.value?.contains(event.target as Node)) closeSelector();
};
const handleSelectorFocusOut = (event: FocusEvent) => {
  if (!selectorRoot.value?.contains(event.relatedTarget as Node | null))
    closeSelector();
};
onMounted(() => document.addEventListener("pointerdown", handleOutsidePointer));
onUnmounted(() =>
  document.removeEventListener("pointerdown", handleOutsidePointer),
);

const navigationItems = computed<GuideNavigationItem[]>(() =>
  props.galleries.map((gallery, index) => ({
    id: String(gallery.product_id),
    label:
      props.guide.products?.find((product) => product.id === gallery.product_id)
        ?.title ||
      gallery.items.find((item) => item.product_title)?.product_title ||
      t("pages.guide.galleryTourFallback", { number: index + 1 }),
  })),
);

const activeGallery = computed(() =>
  props.galleries.find(
    (gallery) => String(gallery.product_id) === activeGalleryId.value,
  ),
);

const selectGallery = (id: string) => {
  if (navigationItems.value.some((item) => item.id === id)) {
    activeMaterialIndex.value = 0;
    activeGalleryId.value = id;
  }
};

const materialCaption = (item: GuideMaterial) => item.caption?.trim() || "";

const materialAccessibleLabel = (item: GuideMaterial, index: number) => {
  const caption = materialCaption(item);
  return caption
    ? `${index + 1}. ${caption}`
    : t("pages.guide.galleryGoToSlide", { number: index + 1 });
};

const selectMaterial = (index: number) => {
  activeMaterialIndex.value = index;
  void nextTick(() => {
    document
      .getElementById(`${props.panelId}-gallery-${activeGalleryId.value}-panel`)
      ?.scrollIntoView?.({ behavior: "auto", block: "start" });
  });
};

const galleryLabel = (productId: number) =>
  navigationItems.value.find((item) => item.id === String(productId))?.label;

watch(
  navigationItems,
  (items) => {
    closeSelector();
    if (!items.some((item) => item.id === activeGalleryId.value)) {
      activeMaterialIndex.value = 0;
      activeGalleryId.value = items[0]?.id ?? "";
    }
  },
  { immediate: true },
);
</script>
